import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface Mandate {
  id: string;
  client: string;
  status: 'processing' | 'completed' | 'needs_review';
  confidence: number;
  documentsProcessed: number;
  totalDocuments: number;
  interventionsNeeded: number;
}

const mandates: Mandate[] = [
  {
    id: "MND-001",
    client: "Innovate Zurich AG",
    status: 'processing',
    confidence: 94,
    documentsProcessed: 1420,
    totalDocuments: 1820,
    interventionsNeeded: 3
  },
  {
    id: "MND-002", 
    client: "Alpine Logistics GmbH",
    status: 'needs_review',
    confidence: 78,
    documentsProcessed: 320,
    totalDocuments: 450,
    interventionsNeeded: 8
  },
  {
    id: "MND-003",
    client: "Swiss Precision SA",
    status: 'completed',
    confidence: 97,
    documentsProcessed: 680,
    totalDocuments: 680,
    interventionsNeeded: 0
  }
];

export const MandateAIOverview = () => {
  const [selectedMandate, setSelectedMandate] = useState<string>(mandates[0].id);
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'needs_review': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'processing': return 'Processing';
      case 'completed': return 'Completed';
      case 'needs_review': return 'Needs Review';
      default: return 'Unknown';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-bold text-brand-primary">AI Processing by Mandate</CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={selectedMandate} onValueChange={setSelectedMandate}>
          <TabsList className="grid w-full grid-cols-3">
            {mandates.map((mandate) => (
              <TabsTrigger 
                key={mandate.id} 
                value={mandate.id}
                className="text-xs"
              >
                {mandate.client.split(' ')[0]}
              </TabsTrigger>
            ))}
          </TabsList>
          
          {mandates.map((mandate) => (
            <TabsContent key={mandate.id} value={mandate.id} className="mt-4">
              <div className="space-y-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium text-sm">{mandate.client}</h4>
                    <p className="text-xs text-muted-foreground">ID: {mandate.id}</p>
                  </div>
                  <Badge variant="secondary" className={getStatusColor(mandate.status)}>
                    {getStatusLabel(mandate.status)}
                  </Badge>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-xs">
                  <div>
                    <p className="text-muted-foreground">AI Confidence</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Progress value={mandate.confidence} className="h-2 flex-1" />
                      <span className="font-medium">{mandate.confidence}%</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Processing Progress</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Progress value={(mandate.documentsProcessed / mandate.totalDocuments) * 100} className="h-2 flex-1" />
                      <span className="font-medium">{mandate.documentsProcessed}/{mandate.totalDocuments}</span>
                    </div>
                  </div>
                </div>
                
                {mandate.interventionsNeeded > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <i className="fa-solid fa-exclamation-triangle text-yellow-600"></i>
                      <span className="text-xs font-medium text-yellow-800">
                        {mandate.interventionsNeeded} intervention{mandate.interventionsNeeded > 1 ? 's' : ''} needed
                      </span>
                    </div>
                  </div>
                )}
                
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="h-7 text-xs flex-1">
                    <i className="fa-solid fa-eye w-3 h-3 mr-1"></i>
                    View Details
                  </Button>
                  {mandate.interventionsNeeded > 0 && (
                    <Button size="sm" className="h-7 text-xs bg-brand-primary hover:bg-brand-primary/90">
                      <i className="fa-solid fa-wrench w-3 h-3 mr-1"></i>
                      Review
                    </Button>
                  )}
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
};